import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

export interface MicroMapProps {
    lng: number;
    lat: number;
    primaryColor: string;
    /**
     * `true` (preview): interactive map with Mapbox **cooperative gestures** —
     *   two fingers to pan on touch, Ctrl/Cmd+scroll to zoom. Avoids the mobile
     *   scroll-trap where a one-finger swipe starting on the map would pan the
     *   map instead of scrolling the page. Mapbox renders its own hint overlay.
     *
     * `false` (full): standard interactive map with a NavigationControl. Used
     *   when the map is the primary surface (e.g. the full map view).
     */
    isPreview: boolean;
}

// NOTE: This component must never add a `mapboxgl.GeolocateControl` — detail
// pages must not prompt for geolocation on load (story 4.1).

/**
 * Renders the embedded Mapbox micro-map. Split into its own module so
 * `mapbox-gl` is dynamically imported (via `React.lazy`) and kept out of the
 * always-loaded main chunk — it only downloads when a map is actually shown.
 */
export default function MicroMap({ lng, lat, primaryColor, isPreview }: MicroMapProps): React.ReactElement {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);

    useEffect(() => {
        if (!mapContainerRef.current) {
            return;
        }

        const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
        if (!mapboxToken) {
            console.warn('VITE_MAPBOX_ACCESS_TOKEN not found in environment');
            return;
        }

        mapboxgl.accessToken = mapboxToken;

        const map = new mapboxgl.Map({
            maxTileCacheSize: 20,
            container: mapContainerRef.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [lng, lat],
            zoom: 14,
            interactive: true,
            // Preview mode: require 2-finger pan / ctrl+scroll zoom so the
            // embedded map doesn't hijack page scroll on mobile. Mapbox shows
            // its own localized hint overlay automatically.
            cooperativeGestures: isPreview,
            attributionControl: false,
        });

        new mapboxgl.Marker({ color: primaryColor })
            .setLngLat([lng, lat])
            .addTo(map);

        if (!isPreview) {
            map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        }

        map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

        mapRef.current = map;

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, [lng, lat, primaryColor, isPreview]);

    return (
        <div
            ref={mapContainerRef}
            className='w-full h-[150px] md:h-64 rounded-lg overflow-hidden shadow-md'
        />
    );
}
